import { useState } from 'react';

import { SERVER_ORIGIN } from '../config';
import { trackEvent } from '../utils/telemetry';

interface AuthUser { id: string; username: string; avatarId: number | null; email: string | null; emailVerified: boolean }
interface AuthSuccess {
  token: string;
  user: AuthUser;
}

// Mirrors STARTER_PACK_CONFIGS in src/game/starter_pack.ts. Kept inline here
// so the Login bundle doesn't drag in the whole starter-pack module.
type StarterPackKind = 'A' | 'B';
const PACK_OPTIONS: ReadonlyArray<{ kind: StarterPackKind; name: string; desc: string }> = [
  { kind: 'A', name: 'Street Operation', desc: 'Drugs, cops, money, cars. Mainstream gangster kit — two made cards already armed.' },
  { kind: 'B', name: 'The Outfit',        desc: 'Jewels, chips, network, iron. Specialist crew — one made per suit (two on contacts).' },
];

type Mode = 'login' | 'register' | 'forgot';

export function Login({ onAuth, onGuestPlay }: { onAuth: (token: string, user: AuthUser) => void; onGuestPlay?: () => void }) {
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [starterPack, setStarterPack] = useState<StarterPackKind>('A');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set on a successful forgot-password submit; tells the user the email is
  // out (response is always 200 even when address doesn't exist — anti-enum).
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await fetch(`${SERVER_ORIGIN}/auth/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        });
        // Always show success regardless of whether the email exists.
        setForgotSent(true);
        return;
      }
      if (mode === 'register') {
        trackEvent('funnel_register_click');
      }
      const res = await fetch(`${SERVER_ORIGIN}/auth/${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          ...(mode === 'register' ? { email, starterPack, acceptedTerms } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error ?? `${mode} failed`);
        return;
      }
      if (mode === 'register') {
        trackEvent('funnel_register_success', { starterPack, mode: 'standard' });
      }
      const ok = data as AuthSuccess;
      onAuth(ok.token, ok.user);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: 'var(--bg-color)', color: 'var(--text-color)',
    }}>
      <h1 style={{ color: 'var(--accent-color)', marginBottom: '0.5rem', fontSize: '3rem' }}>GrandSnatch</h1>
      <h2 style={{ marginBottom: '1.5rem', fontSize: '1rem', color: '#aaa' }}>
        {mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create an account' : 'Reset your password'}
      </h2>

      {mode === 'forgot' && forgotSent ? (
        <div style={{
          maxWidth: 360, padding: 16, background: '#15151f', border: '1px solid #2a2a3a',
          borderRadius: 6, color: '#bbb', fontSize: 13, lineHeight: 1.6, textAlign: 'center',
        }}>
          If the email is registered, a reset link is on the way.<br/>
          Check your inbox (and spam folder).
        </div>
      ) : (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '300px' }}>
          {mode !== 'forgot' && (
            <input
              type="text"
              placeholder="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              autoComplete="username"
              style={inputStyle}
              disabled={busy}
            />
          )}
          {(mode === 'register' || mode === 'forgot') && (
            <input
              type="email"
              placeholder="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus={mode === 'forgot'}
              autoComplete="email"
              style={inputStyle}
              disabled={busy}
              required
            />
          )}
          {mode !== 'forgot' && (
            <input
              type="password"
              placeholder="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              style={inputStyle}
              disabled={busy}
            />
          )}
          {mode === 'register' && (
            <div>
              <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>Choose your starter pack</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {PACK_OPTIONS.map(p => {
                  const selected = starterPack === p.kind;
                  return (
                    <button
                      key={p.kind}
                      type="button"
                      onClick={() => setStarterPack(p.kind)}
                      disabled={busy}
                      style={{
                        flex: 1, textAlign: 'left', padding: '8px 10px',
                        background: selected ? '#1a2a3a' : '#15151f',
                        border: `2px solid ${selected ? 'var(--accent-color)' : '#333'}`,
                        borderRadius: 4, cursor: busy ? 'wait' : 'pointer', color: '#fff',
                        fontFamily: 'inherit', fontSize: 11,
                      }}
                    >
                      <div style={{ fontWeight: 'bold', color: selected ? 'var(--accent-color)' : '#ddd', fontSize: 13 }}>{p.name}</div>
                      <div style={{ color: '#888', fontSize: 10, marginTop: 3, lineHeight: 1.4 }}>{p.desc}</div>
                    </button>
                  );
                })}
              </div>
              <div style={{ color: '#666', fontSize: 10, marginTop: 4, fontStyle: 'italic' }}>
                The other 4 suits stay to discover via run loot.
              </div>
            </div>
          )}
          {mode === 'register' && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              fontSize: 11, color: '#bbb', lineHeight: 1.4, cursor: 'pointer',
            }}>
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                disabled={busy}
                style={{ marginTop: 2, accentColor: 'var(--accent-color)' }}
              />
              <span>
                I confirm I am at least 14 years old and I accept the{' '}
                <a href="/legal/terms" target="_blank" rel="noopener" style={{ color: '#4fc3f7' }}>Terms of Service</a> and the{' '}
                <a href="/legal/privacy" target="_blank" rel="noopener" style={{ color: '#4fc3f7' }}>Privacy Policy</a>.
              </span>
            </label>
          )}
          {error && (
            <div style={{ color: '#e57373', fontSize: '12px', textAlign: 'center' }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={busy
              || (mode === 'forgot' ? !email : (!username || !password))
              || (mode === 'register' && (!email || !acceptedTerms))}
            className="multi-btn"
            style={{ marginTop: '8px' }}
          >
            {busy ? '...' : mode === 'login' ? 'Sign in' : mode === 'register' ? 'Register' : 'Send reset link'}
          </button>
        </form>
      )}

      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
        {mode === 'login' && (
          <>
            <button onClick={() => switchMode('register')} style={linkBtn}>Don't have an account? Register</button>
            <button onClick={() => switchMode('forgot')} style={linkBtn}>Forgot password?</button>
          </>
        )}
        {mode === 'register' && (
          <button onClick={() => switchMode('login')} style={linkBtn}>Already have an account? Sign in</button>
        )}
        {mode === 'forgot' && (
          <button onClick={() => switchMode('login')} style={linkBtn}>Back to sign in</button>
        )}
      </div>

      {/* Guest CTA — only shown on login/register, not forgot */}
      {onGuestPlay && mode !== 'forgot' && (
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          <div style={{ color: '#444', fontSize: 11, marginBottom: 8 }}>― or ―</div>
          <button
            onClick={onGuestPlay}
            style={{
              background: 'none', border: '1px solid #555',
              color: '#aaa', cursor: 'pointer', fontFamily: 'inherit',
              padding: '8px 20px', borderRadius: 4, fontSize: 13,
              transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { (e.target as HTMLButtonElement).style.borderColor = 'var(--accent-color)'; (e.target as HTMLButtonElement).style.color = '#fff'; }}
            onMouseLeave={e => { (e.target as HTMLButtonElement).style.borderColor = '#555'; (e.target as HTMLButtonElement).style.color = '#aaa'; }}
          >
            Play without account
          </button>
          <div style={{ color: '#555', fontSize: 10, marginTop: 5 }}>
            Tutorial match — no account needed
          </div>
        </div>
      )}

      <div style={{
        position: 'absolute', bottom: 12, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', gap: 14, fontSize: 11, color: '#666',
      }}>
        <a href="/legal/privacy" target="_blank" rel="noopener" style={{ color: '#777', textDecoration: 'none' }}>Privacy</a>
        <span>·</span>
        <a href="/legal/terms" target="_blank" rel="noopener" style={{ color: '#777', textDecoration: 'none' }}>Terms</a>
        <span>·</span>
        <a href="/legal/cookies" target="_blank" rel="noopener" style={{ color: '#777', textDecoration: 'none' }}>Cookies</a>
      </div>
    </div>
  );

  function switchMode(next: Mode) {
    setError(null);
    setForgotSent(false);
    setMode(next);
  }
}

const inputStyle: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: '14px',
  fontFamily: 'inherit',
  background: '#1a1a24',
  border: '1px solid #444',
  borderRadius: '4px',
  color: 'var(--text-color)',
};

const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none',
  color: '#4fc3f7', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px',
  fontFamily: 'inherit', padding: 0,
};
